'use client';

/**
 * MobilePinnedStack — the mobile pinned-card entry point (design turn 14),
 * composing 14a/14b (compact anatomy + swipe, delegated to
 * PinnedCourseCompact/PinnedMediaCompact) with 14c's peekable history
 * stack. Renders nothing when the history is empty; a SINGLE pin renders
 * exactly 14a/14b with no stack chrome at all ("Pinned this call" header,
 * peek edges) — that chrome only earns its keep once there's a second pin
 * to peek at, per the brief.
 *
 * The front pin's body is mounted keyed by its entry `id` (FrontPinBody)
 * so a fresh front pin — a new tool result, OR a stack promotion via
 * bringPinToFront — always starts its own item-swipe index at 0 rather
 * than inheriting a stale index left over from whatever was front before
 * it.
 *
 * Takes `pinHistory`/`dismissPin`/`bringPinToFront` as props rather than
 * reading ChatContext directly — keeps this (and its RTL tests) decoupled
 * from the full provider tree; pinned-card.tsx wires it to context.
 */
import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Flag,
  Layers,
  Play,
} from 'lucide-react';
import type { PinEntry, PinRowKind } from '@/lib/pin-history';
import {
  derivePinRowInfo,
  frontBadgeLabel,
  pinRowKindLabel,
} from '@/lib/pin-history';
import { formatRelativeTime, cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { PinnedCourseCompact } from './pinned-course-compact';
import { PinnedMediaCompact } from './pinned-media-compact';

/** Collapsed state peeks at most this many older-pin edges behind the front
 *  card (the design's two-sliver depth), even if the history holds more. */
const MAX_PEEK = 2;

export interface MobilePinnedStackProps {
  pinHistory: PinEntry[];
  dismissPin: () => void;
  bringPinToFront: (id: string) => void;
}

export function MobilePinnedStack({
  pinHistory,
  dismissPin,
  bringPinToFront,
}: MobilePinnedStackProps) {
  const [expanded, setExpanded] = useState(false);
  const hasStack = pinHistory.length > 1;

  // Defensive reset: if the stack collapses down to a single pin (or zero)
  // while expanded, a LATER second pin arriving mid-session must not
  // suddenly reappear already-expanded from stale state.
  useEffect(() => {
    if (!hasStack) setExpanded(false);
  }, [hasStack]);

  if (pinHistory.length === 0) return null;

  const [front, ...older] = pinHistory;
  const badgeLabel = frontBadgeLabel(front.card, pinHistory.length, expanded);

  const handlePromote = (id: string) => {
    bringPinToFront(id);
    setExpanded(false);
  };

  return (
    <div className="flex flex-col gap-2 px-6 pb-[16px]">
      {hasStack && (
        <div className="flex items-center justify-between px-0.5">
          <span className="text-[12px] font-semibold text-[#8a7c68]">
            Pinned this call
          </span>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-1 rounded-[20px] bg-[#E36001] px-[12px] py-[6px] text-[12.5px] font-bold text-white"
          >
            <Layers className="size-3" aria-hidden="true" />
            {expanded ? 'Collapse' : `${pinHistory.length} pins`}
            <ChevronDown
              className={cn(
                'size-3.5 transition-transform',
                expanded && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>
        </div>
      )}

      {/* mb reserves room for the peek slivers extending past the front
          card's bottom edge (below) — without it they'd overlap whatever
          the column renders next. */}
      <div className={cn('relative', hasStack && !expanded && 'mb-[22px]')}>
        {/* Peek edges (collapsed only) — decorative slivers of the next
            older pins BEHIND the front card, anchored to its BOTTOM so a
            band of each sticks out below it (the front card is opaque and
            ~150px tall, so top-anchored offsets this small would be fully
            covered and invisible). Must never eat pointer events: they're
            not tappable in the collapsed state (expand the stack first to
            reach them as real rows). */}
        {hasStack && !expanded && (
          <>
            {older.slice(0, MAX_PEEK).map((entry, i) => (
              <div
                key={entry.id}
                data-testid="pin-peek-edge"
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-[6px] bottom-0 h-[40px] rounded-[14px] border-[1px] border-[#efe3d2] bg-white"
                style={{
                  transform: `translateY(${i === 0 ? 12 : 24}px) scale(${i === 0 ? 0.955 : 0.915})`,
                  opacity: i === 0 ? 0.9 : 0.72,
                  zIndex: i === 0 ? 2 : 1,
                }}
              />
            ))}
          </>
        )}

        <div className="relative z-10">
          <FrontPinBody
            key={front.id}
            entry={front}
            badgeLabel={badgeLabel}
            onDismiss={dismissPin}
          />
        </div>
      </div>

      {hasStack && expanded && (
        <div className="flex flex-col gap-2">
          {older.map((entry) => (
            <StackRow
              key={entry.id}
              entry={entry}
              onPromote={() => handlePromote(entry.id)}
            />
          ))}
        </div>
      )}

      {/* The turn-11 "Sara pinned this" chip only applies when there's no
          stack chrome to carry that context instead — a lone pin is exactly
          14a/14b, unchanged from before turn 14. */}
      {!hasStack && (
        <span className="self-center rounded-[20px] bg-white/60 px-[12px] py-0.5 text-[11px] text-[#b3a996]">
          Sara pinned this · conversation continues
        </span>
      )}
    </div>
  );
}

function FrontPinBody({
  entry,
  badgeLabel,
  onDismiss,
}: {
  entry: PinEntry;
  badgeLabel: string;
  onDismiss: () => void;
}) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.22, ease: 'easeOut' }}
    >
      {entry.card.kind === 'courses' ? (
        <PinnedCourseCompact
          courses={entry.card.courses}
          onDismiss={onDismiss}
          badgeLabel={badgeLabel}
        />
      ) : (
        <PinnedMediaCompact
          refs={entry.card.refs}
          onDismiss={onDismiss}
          badgeLabel={badgeLabel}
        />
      )}
    </motion.div>
  );
}

/** Icon tile per row kind (14c's expanded rows) — video: warm bg + orange
 *  play; article: cool bg + blue doc glyph; course: warm bg + orange
 *  pin/flag (the SAME flag glyph PinnedCardShell's badge uses, for visual
 *  continuity between "this is a course pin" surfaces). Article/course
 *  reuse the SAME warm/cool tile tokens date-tile.tsx already established
 *  for in-person-vs-online course tiles (dateTileInPersonBg/OnlineBg) —
 *  video's tile is the one genuinely new value the design brief introduces. */
const ROW_TILE: Record<PinRowKind, { bg: string; icon: React.ReactNode }> = {
  video: {
    bg: '#FBEBDA',
    icon: (
      <Play
        className="size-4"
        fill={V3_COLORS.orange}
        stroke={V3_COLORS.orange}
      />
    ),
  },
  article: {
    bg: V3_COLORS.dateTileOnlineBg,
    icon: <FileText className="size-4" style={{ color: '#4A6DA6' }} />,
  },
  course: {
    bg: V3_COLORS.dateTileInPersonBg,
    icon: <Flag className="size-4" style={{ color: V3_COLORS.orange }} />,
  },
};

function StackRow({
  entry,
  onPromote,
}: {
  entry: PinEntry;
  onPromote: () => void;
}) {
  const { kind, title } = derivePinRowInfo(entry.card);
  const tile = ROW_TILE[kind];

  return (
    <button
      type="button"
      onClick={onPromote}
      data-testid="pin-stack-row"
      className="flex items-center gap-3 rounded-[14px] border-[1px] border-[#efe3d2] bg-white p-[11px] px-[13px] text-left"
    >
      <span
        className="flex size-9 shrink-0 items-center justify-center rounded-[9px]"
        style={{ backgroundColor: tile.bg }}
        aria-hidden="true"
      >
        {tile.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-[#31364E]">
          {title}
        </span>
        <span className="block text-[11.5px] text-[#9a8f7f]">
          {pinRowKindLabel(kind)} ·{' '}
          {formatRelativeTime(new Date(entry.pinnedAt).toISOString())}
        </span>
      </span>
      <ChevronRight
        className="size-4 shrink-0"
        style={{ color: '#c9a487' }}
        aria-hidden="true"
      />
    </button>
  );
}

export default MobilePinnedStack;
